/**
 * App accent palette — user-selectable accent colour for the APP CHROME
 * (buttons, links, active nav chips, focus rings, accents). This is the
 * APP-UI accent, NOT the report ReportTheme/cover branding (that is a
 * separate system under src/types/reportTheme + reportThemeService).
 *
 * ── How the swap works (token-level, zero per-component hex) ─────────────
 * The whole app accent flows from the `--cyan-*` ramp declared in index.css
 * (Aurora's teal is anchored on `--cyan-500`): `--primary`, `--primary-hover`,
 * `--primary-active`, `--ring`, `--info`, `--chart-1`, `--sidebar-primary`,
 * `--sidebar-ring` all `var(--cyan-*)`. So re-mapping the ramp under a
 * `[data-accent="..."]` selector re-skins the accent app-wide in ONE place.
 * The per-palette CSS variable sets live in src/index.css under
 * `[data-accent="x"]` (dark) + `.light[data-accent="x"]` (light) selectors;
 * this module only owns the registry metadata + persistence + the
 * `<html data-accent="…">` attribute swap. The picker just sets the
 * attribute — never a colour literal in a component.
 *
 * Default = Teal = the CURRENT Aurora accent. The default carries NO
 * `data-accent` attribute (or `data-accent="teal"`, which declares no
 * override), so existing users see ZERO change unless they pick a palette.
 */

export type AccentPaletteId = 'teal' | 'indigo' | 'cyan' | 'slate';

export interface AccentPaletteMeta {
  id: AccentPaletteId;
  /** Human label shown in the picker. */
  name: string;
  /** One-line colour-theory rationale (tooltip / a11y). */
  description: string;
  /**
   * Representative swatch token — the Tailwind/CSS custom property the swatch
   * preview reads so the chip is painted from the SAME token the palette
   * re-maps (no hand-rolled hex in the component). Read at render time AFTER
   * the palette attribute is applied to a scoped element.
   */
  swatchVar: '--cyan-500';
}

export const DEFAULT_ACCENT: AccentPaletteId = 'teal';

/**
 * The 5 palettes. Each is a coherent accent ramp (base + hover + active +
 * focus ring + glow wash) that works on BOTH Aurora dark (near-black canvas)
 * and Aurora light (paper). Colour-theory grounded — see `description`.
 * Order is the picker order; `teal` is first (default).
 */
export const ACCENT_PALETTES: readonly AccentPaletteMeta[] = [
  {
    id: 'teal',
    name: 'Teal',
    description: 'Aurora signature — cool, calm brand anchor (default).',
    swatchVar: '--cyan-500',
  },
  {
    id: 'indigo',
    name: 'Indigo',
    description: 'Analogous-cool blue — deep, technical, crisp glow on dark.',
    swatchVar: '--cyan-500',
  },
  {
    id: 'cyan',
    name: 'Cyan',
    description: 'Bright sky cyan — crisp, technical sibling of teal.',
    swatchVar: '--cyan-500',
  },
  {
    id: 'slate',
    name: 'Slate',
    description: 'Neutral steel — restrained, professional, low-chroma.',
    swatchVar: '--cyan-500',
  },
] as const;

const VALID_IDS: ReadonlySet<string> = new Set(ACCENT_PALETTES.map((p) => p.id));

export function isAccentPaletteId(value: unknown): value is AccentPaletteId {
  return typeof value === 'string' && VALID_IDS.has(value);
}

export const ACCENT_STORAGE_KEY = 'da-accent';

/** Stored user choice (defaults to the brand teal on first visit). */
export function getStoredAccentPalette(): AccentPaletteId {
  if (typeof window === 'undefined' || typeof localStorage === 'undefined') return DEFAULT_ACCENT;
  try {
    const stored = localStorage.getItem(ACCENT_STORAGE_KEY);
    if (isAccentPaletteId(stored)) return stored;
  } catch {
    /* private mode */
  }
  return DEFAULT_ACCENT;
}

/**
 * Stamp `<html data-accent="…">`. The default Teal palette REMOVES the
 * attribute (its tokens are the index.css :root/.light defaults), so a
 * default user's DOM is byte-identical to pre-feature.
 */
export function applyResolvedAccent(id: AccentPaletteId): void {
  if (typeof document === 'undefined') return;
  const root = document.documentElement;
  if (id === DEFAULT_ACCENT) root.removeAttribute('data-accent');
  else root.setAttribute('data-accent', id);
}

/** Apply + persist the chosen palette. Survives reload via localStorage. */
export function applyAccentPalette(id: AccentPaletteId): void {
  applyResolvedAccent(id);
  try {
    localStorage.setItem(ACCENT_STORAGE_KEY, id);
  } catch {
    /* private mode */
  }
}

/**
 * Inline bootstrap for index.html — applies the stored accent BEFORE React
 * paints (mirrors getStoredAccentPalette + applyResolvedAccent), so the very
 * first paint already carries the chosen accent (no teal flash). Keep in
 * lock-step with the two functions above.
 */
export const ACCENT_BOOTSTRAP_SCRIPT = `(function(){try{var a=localStorage.getItem('da-accent');if(a&&a!=='teal'&&['indigo','cyan','slate'].indexOf(a)>=0){document.documentElement.setAttribute('data-accent',a);}}catch(e){}})();`;
